import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  PaymentInformationServiceErrorV1Response,
  PaymentPreviewErrorPayload,
  PaymentRulesResponseV1,
} from '../models';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from '@arc/aureus';

@Injectable({
  providedIn: 'root',
})
export class PaymentInformationErrorHelperService {
  constructor(
    private translationService: TranslateService,
    private messageService: MessageService,
  ) {}

  public getPaymentPreviewErrorActionPayload(
    error: HttpErrorResponse,
  ): PaymentPreviewErrorPayload {
    const surchargeErrors = this.parsePaymentPreviewResponseSurchargeErrors(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (error.error?.errors || []) as PaymentInformationServiceErrorV1Response[],
    );

    if (surchargeErrors === null) {
      const payload: PaymentPreviewErrorPayload = {
        isSelectedCardRestrictedByZipCode: false,
        isSelectedCardRestrictedByType: false,
        error,
      };
      return payload;
    }

    surchargeErrors.error = error.error as unknown;

    return surchargeErrors;
  }

  public displayPaymentPreviewErrorMessage(
    error: PaymentPreviewErrorPayload,
    paymentRules: PaymentRulesResponseV1,
    tenantName: string,
  ) {
    const message = this.getSurchargeUserErrorMessage(
      error,
      tenantName,
      paymentRules,
    );

    // this is a TEMPORARY fix to avoid show multiple messages because we are calling multiple times the preview endpoint.
    // We are using this to show the message and dismiss others with the same text
    // see: https://aureus.aws-dev.billtrust.com/components/message
    this.messageService.open(message, null, {
      severity: 'danger',
      channel: message,
      duration: 0,
      dismissOnNavigate: true,
      dismissOthers: true,
    });
  }

  private parsePaymentPreviewResponseSurchargeErrors(
    previewErrors: PaymentInformationServiceErrorV1Response[],
  ): PaymentPreviewErrorPayload {
    const surchargeError = previewErrors.find(
      (e) =>
        e.code
          ?.toLowerCase()
          .includes('surcharge.api.call.response.has.errors'),
    );

    if (!surchargeError) {
      return null;
    }

    // The response contains a string containing comma-separated properties: "ReasonCode: SOME_CODE, Errors: Some error string"
    const messageDetails = surchargeError.message.split(', ');
    const reasonCode = messageDetails
      .find((x) => x.startsWith('ReasonCode: '))
      .toUpperCase();
    const reasonErrors = surchargeError.message
      .substring(surchargeError.message.lastIndexOf('Errors: '))
      .toLowerCase()
      .replace(/\s/gi, '');

    const ineligibleForSurcharging = reasonCode.includes(
      'INELIGIBLE_FOR_SURCHARGING',
    );
    const invalidOrUnsupportedZipCode = reasonCode.includes(
      'INVALID_OR_UNSUPPORTED_ZIP_CODE',
    );
    const invalidOrUnsupportedCountryCode = reasonCode.includes(
      'INVALID_OR_UNSUPPORTED_COUNTRY_CODE',
    );
    const hasMissingValidRegionOrStateReason = reasonErrors.includes(
      'missingvalidregionaszipcodeorstate',
    );
    const interpaymentsErrorCode = reasonCode.includes('INTERPAYMENTS_ERROR');
    const isInterpaymentsRegionRestricted =
      interpaymentsErrorCode && hasMissingValidRegionOrStateReason;
    // This is a temp change until these code are made specific to a reason error
    const hasSurchargeRestrictions =
      ineligibleForSurcharging || invalidOrUnsupportedZipCode;

    const isSelectedCardRestrictedByZipCode =
      (hasSurchargeRestrictions &&
        (reasonErrors.includes('regionrestricted') ||
          hasMissingValidRegionOrStateReason)) ||
      invalidOrUnsupportedCountryCode ||
      isInterpaymentsRegionRestricted;

    const isSelectedCardRestrictedByType =
      hasSurchargeRestrictions && reasonErrors.includes('cardtyperestricted');

    const payload: PaymentPreviewErrorPayload = {
      isSelectedCardRestrictedByZipCode,
      isSelectedCardRestrictedByType,
      error: null,
    };
    return payload;
  }

  private getSurchargeUserErrorMessage(
    error: PaymentPreviewErrorPayload,
    tenantName: string,
    paymentRules: PaymentRulesResponseV1,
  ): string {
    const isAchEnabled = paymentRules.settings.payments?.acceptBank;

    let userErrorMessage = this.translationService.instant(
      isAchEnabled
        ? 'ARC_PAYMENT_SURCHARGE_UNKNOWN_ERROR_WITH_ACH'
        : 'ARC_PAYMENT_SURCHARGE_UNKNOWN_ERROR',
    ) as string;

    if (
      error.isSelectedCardRestrictedByZipCode &&
      error.isSelectedCardRestrictedByType
    ) {
      userErrorMessage = this.translationService.instant(
        isAchEnabled
          ? 'ARC_PAYMENT_RESTRICT_BY_PAYMENT_TYPE_ZIP_CODE_ERROR_WITH_ACH'
          : 'ARC_PAYMENT_RESTRICT_BY_PAYMENT_TYPE_ZIP_CODE_ERROR',
        { companyName: tenantName },
      ) as string;
    } else if (error.isSelectedCardRestrictedByZipCode) {
      userErrorMessage = this.translationService.instant(
        isAchEnabled
          ? 'ARC_PAYMENT_RESTRICT_BY_ZIP_CODE_ONLY_ERROR_WITH_ACH'
          : 'ARC_PAYMENT_RESTRICT_BY_ZIP_CODE_ONLY_ERROR',
        { companyName: tenantName },
      ) as string;
    } else if (error.isSelectedCardRestrictedByType) {
      userErrorMessage = this.translationService.instant(
        isAchEnabled
          ? 'ARC_PAYMENT_RESTRICT_BY_PAYMENT_TYPE_ONLY_ERROR_WITH_ACH'
          : 'ARC_PAYMENT_RESTRICT_BY_PAYMENT_TYPE_ONLY_ERROR',
        { companyName: tenantName },
      ) as string;
    }

    return userErrorMessage;
  }
}
